/**
 * Componente para gestionar el personal asignado a un evento/presupuesto
 * Permite ver, añadir y eliminar empleados con sus roles
 */

import React, { useState, useEffect } from 'react';
import { Users, Plus, X, UserCheck, AlertTriangle, ChefHat, User, Briefcase } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { esPersonaEventos } from '../utils/empleadosEventos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES_DISPONIBLES = [
  { id: 'responsable', nombre: 'Responsable de Evento', icon: Briefcase, color: 'bg-purple-100 text-purple-700' },
  { id: 'cocinero', nombre: 'Cocinero/a', icon: ChefHat, color: 'bg-orange-100 text-orange-700' },
  { id: 'camarero', nombre: 'Camarero/a', icon: User, color: 'bg-blue-100 text-blue-700' },
  { id: 'ayudante', nombre: 'Ayudante', icon: Users, color: 'bg-gray-100 text-gray-700' },
  { id: 'montaje', nombre: 'Montaje/Desmontaje', icon: Users, color: 'bg-green-100 text-green-700' }
];

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;

// onGuardado (opcional): 'inicio' | 'ok' | 'error' alrededor de cada guardado
// variante 'nuevo': diseño de tarjetas de la ficha nueva (mismas funciones)
const PersonalAsignado = ({ presupuestoId, presupuestoNumero, personalActual = [], onUpdate, inTab = false, onGuardado, variante = null, encolar = null }) => {
  const nuevo = variante === 'nuevo';
  const { token } = useAuth();
  const [empleados, setEmpleados] = useState([]);
  const [personal, setPersonal] = useState(personalActual || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState('');
  const [selectedRol, setSelectedRol] = useState('camarero');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // Formato nuevo (`encolar`): el guardado va por la cola de la ficha (nunca dos
  // guardados del presupuesto a la vez). Sin `encolar`, como siempre.
  const enCola = async (clave, hacer) => {
    if (!encolar) return hacer();
    const r = await encolar(clave, async () => {
      try {
        return { res: await hacer() };
      } catch (error) {
        return { error };
      }
    });
    if (r && r.error) throw r.error;
    return r ? r.res : undefined;
  };

  // Cargar lista de empleados
  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        const res = await fetch(`${API_URL}/api/empleados`, { headers });
        const data = await res.json();
        if (Array.isArray(data)) {
          // Sin cuentas técnicas (p. ej. "admin"): no son personas de eventos
          setEmpleados(data.filter(e => e.activo && esPersonaEventos(e)));
        }
      } catch (e) {
        console.error('Error cargando empleados:', e);
      }
    };
    fetchEmpleados();
  }, [token]);

  // Actualizar cuando cambie personalActual
  useEffect(() => {
    setPersonal(personalActual || []);
  }, [personalActual]);

  // Empleados disponibles (no asignados)
  const empleadosDisponibles = empleados.filter(e => 
    !personal.some(p => p.empleado_id === e.id)
  );

  // Filtrar empleados por búsqueda
  const empleadosFiltrados = empleadosDisponibles.filter(e =>
    e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.rol?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAsignarPersonal = async () => {
    if (!selectedEmpleado) return;
    
    setLoading(true);
    let guardando = false;
    try {
      const empleadoData = empleados.find(e => e.id === selectedEmpleado);
      if (!empleadoData) return;

      const nuevoPersonal = [...personal, {
        empleado_id: selectedEmpleado,
        nombre: empleadoData.nombre,
        rol: selectedRol,
        fecha_asignacion: new Date().toISOString()
      }];

      // Guardar en backend
      if (onGuardado) { onGuardado('inicio'); guardando = true; }
      const res = await enCola('personal_asignado', () => fetch(`${API_URL}/api/presupuestos/${presupuestoId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ personal_asignado: nuevoPersonal })
      }));
      if (guardando) { onGuardado(res.ok ? 'ok' : 'error'); guardando = false; }

      if (res.ok) {
        setPersonal(nuevoPersonal);
        setShowAddModal(false);
        setSelectedEmpleado('');
        setSelectedRol('camarero');
        setSearchTerm('');
        if (onUpdate) onUpdate(nuevoPersonal);
      }
    } catch (e) {
      if (guardando) onGuardado('error');
      console.error('Error asignando personal:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarPersonal = async (empleadoId) => {
    if (!window.confirm('¿Eliminar este empleado del evento?')) return;
    
    setLoading(true);
    let guardando = false;
    try {
      const nuevoPersonal = personal.filter(p => p.empleado_id !== empleadoId);

      if (onGuardado) { onGuardado('inicio'); guardando = true; }
      const res = await enCola('personal_asignado', () => fetch(`${API_URL}/api/presupuestos/${presupuestoId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ personal_asignado: nuevoPersonal })
      }));
      if (guardando) { onGuardado(res.ok ? 'ok' : 'error'); guardando = false; }

      if (res.ok) {
        setPersonal(nuevoPersonal);
        if (onUpdate) onUpdate(nuevoPersonal);
      }
    } catch (e) {
      if (guardando) onGuardado('error');
      console.error('Error eliminando personal:', e);
    } finally {
      setLoading(false);
    }
  };

  const getRolInfo = (rolId) => {
    return ROLES_DISPONIBLES.find(r => r.id === rolId) || ROLES_DISPONIBLES[2]; // Default: camarero
  };

  const modal = showAddModal && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Asignar Personal</h3>
            <button
              onClick={() => setShowAddModal(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Búsqueda de empleado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Empleado
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre o rol..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Selector de empleado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empleado
              </label>
              <select
                value={selectedEmpleado}
                onChange={(e) => setSelectedEmpleado(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Seleccionar empleado...</option>
                {empleadosFiltrados.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.nombre} - {e.rol || 'Sin rol definido'}
                  </option>
                ))}
              </select>
              {empleadosDisponibles.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  Todos los empleados ya están asignados
                </p>
              )}
            </div>

            {/* Selector de rol para el evento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol en este Evento
              </label>
              <select
                value={selectedRol}
                onChange={(e) => setSelectedRol(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {ROLES_DISPONIBLES.map(rol => (
                  <option key={rol.id} value={rol.id}>
                    {rol.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowAddModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleAsignarPersonal}
              disabled={!selectedEmpleado || loading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <UserCheck className="w-4 h-4" />
              )}
              Asignar
            </button>
          </div>
        </div>
      </div>
  );

  // Formato nuevo: filas compactas en la tarjeta "Personal asignado"
  if (nuevo) {
    return (
      <div className="flex flex-col gap-3" data-testid="personal-asignado">
        {personal.length === 0 ? (
          <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="personal-asignado-vacio">
            <strong>Sin personal asignado.</strong> Asigna las personas del equipo que van al evento.
          </div>
        ) : (
          <div className="border border-[#E1E6E3] rounded-lg divide-y divide-[#EEF1EF]">
            {personal.map((p) => (
              <div key={p.empleado_id || p.nombre} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`personal-asignado-${p.empleado_id}`}>
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-[#16221D]">{p.nombre}</span>
                  <span className="block text-xs text-[#5B6B64]">
                    {getRolInfo(p.rol).nombre}{p.origen === 'quien_va' ? ' · elegido en Quién va al evento' : ''}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleEliminarPersonal(p.empleado_id)}
                  disabled={loading}
                  className="text-xs font-semibold text-[#8A1C12] hover:underline disabled:opacity-50"
                  title="Eliminar del evento"
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#F7F9F8] px-3.5 py-3 text-sm">
          <span className="text-[#5B6B64]">
            {personal.length === 0 ? 'Nadie asignado' : `${personal.length} ${personal.length === 1 ? 'persona asignada' : 'personas asignadas'}`}
          </span>
          <button type="button" onClick={() => setShowAddModal(true)} className={botonPrincipal} data-testid="personal-asignado-asignar">
            Asignar persona
          </button>
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div className={inTab ? '' : 'bg-white rounded-lg shadow-sm border border-gray-200 p-5'}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`${inTab ? 'text-base' : 'text-lg'} font-bold text-gray-900 flex items-center gap-2`}>
          <Users className="w-5 h-5 text-indigo-600" />
          Personal Asignado
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Asignar
        </button>
      </div>

      {/* Alerta si no hay personal */}
      {personal.length === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Sin personal asignado</p>
            <p className="text-red-600 text-sm">Este evento no tiene personal asignado. Asigna empleados para su correcta ejecución.</p>
          </div>
        </div>
      )}

      {/* Lista de personal asignado */}
      {personal.length > 0 && (
        <div className="space-y-2">
          {personal.map((p, idx) => {
            const rolInfo = getRolInfo(p.rol);
            const IconComponent = rolInfo.icon;
            return (
              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${rolInfo.color}`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{p.nombre}</p>
                    <p className="text-xs text-gray-500">{rolInfo.nombre}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleEliminarPersonal(p.empleado_id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Eliminar del evento"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Resumen */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center text-sm">
        <span className="text-gray-500">Total asignados:</span>
        <span className={`font-bold ${personal.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {personal.length} persona{personal.length !== 1 ? 's' : ''}
        </span>
      </div>

      {modal}
    </div>
  );
};

export default PersonalAsignado;
